import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import SegmentedButtons from '@gumtree/ui-library-v2/src/segmented-buttons/segmented-buttons';
import { LeadFormItemProps, LeadFormItemErrorMessages } from './LeadFormItem.type';
import {
    ItemContainer,
    Label,
    ErrorText,
    InputBox,
    SelectBox,
    TextAreaBox,
    SegmentedButtonsWrapper,
    CheckboxGroup,
    CheckboxLabel,
} from './LeadFormItem.style';

const LeadFormItem = forwardRef<any, LeadFormItemProps>((props, ref) => {
    const {
        type,
        label,
        name,
        required,
        requiredLabel = false,
        maxLength,
        minLength,
        pattern,
        validator,
        errorMessage,
        options = [],
        value: propValue,
        onChange,
        placeholder,
        disabled,
        otherValue: otherValueProp,
        onOtherValueChange,
    } = props;

    const [value, setValue] = useState<string | number | string[]>(propValue ?? (type === 'checkbox' ? [] : ''));
    const [error, setError] = useState<string | null>(null);
    const [otherValue, setOtherValue] = useState<string>('');

    useEffect(() => {
        if (type === 'checkbox') {
            const selected = Array.isArray(propValue) ? propValue : [];
            const otherOpt = options.find(o => o.label.toLowerCase() === 'other');
            const shouldSelectOther = !!otherValueProp && !!otherOpt && !selected.includes(otherOpt.value);
            const nextSelected = shouldSelectOther ? [...selected, otherOpt!.value] : selected;
            setValue(nextSelected);
        } else {
            setValue((propValue as string | number) ?? '');
        }

        if (otherValueProp !== undefined) {
            setOtherValue(otherValueProp);
        }
    }, [propValue, type, otherValueProp, options]);

    const getErrorMessage = (typeKey: 'required' | 'minLength' | 'pattern'): string => {
        if (typeof errorMessage === 'object' && errorMessage !== null) {
            const errorMessages = errorMessage as LeadFormItemErrorMessages;
            switch (typeKey) {
                case 'required':
                    return errorMessages.required || 'This field is required';
                case 'minLength':
                    return errorMessages.minLength || `Minimum ${minLength} characters required`;
                case 'pattern':
                    return errorMessages.pattern || 'Invalid format';
                default:
                    return 'Invalid input';
            }
        }

        if (typeof errorMessage === 'string') {
            return errorMessage;
        }

        switch (typeKey) {
            case 'required':
                return 'This field is required';
            case 'minLength':
                return `Minimum ${minLength} characters required`;
            case 'pattern':
                return 'Invalid format';
            default:
                return 'Invalid input';
        }
    };

    const isOtherSelected = () => {
        if (type === 'checkbox' && Array.isArray(value)) {
            const lowerLabelByValue = new Map(options.map((o) => [o.value, o.label.toLowerCase()]));
            return (value as string[]).some((v) => (lowerLabelByValue.get(v) || '').toLowerCase() === 'other');
        }
        if (type === 'select' && value) {
            const selectedOption = options.find(opt => opt.value === String(value));
            return selectedOption ? selectedOption.label.toLowerCase() === 'other' : false;
        }
        return false;
    };

    const doValidate = () => {
        if (type === 'checkbox') {
            const selected = Array.isArray(value) ? value : [];
            if (required && selected.length === 0) {
                setError(getErrorMessage('required'));
                return false;
            }
            if (isOtherSelected() && validator) {
                const validationResult = validator(otherValue);
                if (typeof validationResult === 'string') {
                    setError(validationResult);
                    return false;
                } else if (validationResult === false) {
                    setError('Invalid input');
                    return false;
                }
            }
            setError(null);
            return true;
        }

        if (type === 'select') {
            if (required && !value) {
                setError(getErrorMessage('required'));
                return false;
            }
            if (isOtherSelected() && validator) {
                const validationResult = validator(otherValue);
                if (typeof validationResult === 'string') {
                    setError(validationResult);
                    return false;
                } else if (validationResult === false) {
                    setError('Invalid input');
                    return false;
                }
            }
            if (validator && value && !isOtherSelected()) {
                const validationResult = validator(value as string | number);
                if (typeof validationResult === 'string') {
                    setError(validationResult);
                    return false;
                } else if (validationResult === false) {
                    setError('Invalid input');
                    return false;
                }
            }
            setError(null);
            return true;
        }

        if (required && !value) {
            setError(getErrorMessage('required'));
            return false;
        }
        if (minLength && value && String(value).length < minLength) {
            setError(getErrorMessage('minLength'));
            return false;
        }
        if (pattern && value && !pattern.test(String(value))) {
            setError(getErrorMessage('pattern'));
            return false;
        }
        if (validator && value) {
            const validationResult = validator(value as string | number);
            if (typeof validationResult === 'string') {
                setError(validationResult);
                return false;
            } else if (validationResult === false) {
                setError('Invalid input');
                return false;
            }
        }
        setError(null);
        return true;
    };

    useImperativeHandle(ref, () => ({
        validate: doValidate,
        getValue: () => value,
        setValue: (v: string | number | string[]) => setValue(v),
        setError: (msg: string) => setError(msg),
        clearError: () => setError(null),
        props,
    }));

    const handleChange = (v: string | number | string[]) => {
        setValue(v);
        setError(null);
        
        if (type === 'checkbox') {
            const newValue = Array.isArray(v) ? v : [];
            const lowerLabelByValue = new Map(options.map((o) => [o.value, o.label.toLowerCase()]));
            const hasOther = newValue.some((val) => (lowerLabelByValue.get(val) || '').toLowerCase() === 'other');
            if (!hasOther) {
                setOtherValue('');
            }
        } else if (type === 'select') {
            const selectedOption = options.find(opt => opt.value === String(v));
            const isOther = selectedOption ? selectedOption.label.toLowerCase() === 'other' : false;
            if (!isOther) {
                setOtherValue('');
                onOtherValueChange?.('');
            }
        }

        onChange?.(v);
    };

    const handleBlur = () => {
        doValidate();
    };

    const toggleCheckbox = (optValue: string) => {
        const current = Array.isArray(value) ? value : [];
        let next: string[];

        if (current.includes(optValue)) {
            next = current.filter((v) => v !== optValue);

            const selectedOption = options.find(opt => opt.value === optValue);
            if (selectedOption && selectedOption.label.toLowerCase() === 'other') {
                setOtherValue('');
                onOtherValueChange?.('');
            }
        } else {
            next = [...current, optValue];
        }
        handleChange(next);
    };

    return (
        <ItemContainer isSegmented={type === 'segmented'}>
            <Label error={!!error} isSegmented={type === 'segmented'}>
                {label}
                {required && requiredLabel && ' *'}
            </Label>
            {type === 'input' && (
                <InputBox
                    name={name}
                    value={value as string | number}
                    onChange={(e) => handleChange(e.target.value)}
                    onBlur={handleBlur}
                    maxLength={maxLength}
                    minLength={minLength}
                    placeholder={placeholder}
                    disabled={disabled}
                    error={!!error}
                />
            )}
            {type === 'textarea' && (
                <TextAreaBox
                    name={name}
                    value={value as string | number}
                    onChange={(e) => handleChange(e.target.value)}
                    onBlur={handleBlur}
                    maxLength={maxLength}
                    minLength={minLength}
                    placeholder={placeholder}
                    disabled={disabled}
                    error={!!error}
                />
            )}
            {type === 'select' && (
                <div>
                    <SelectBox
                        name={name}
                        value={value as string | number}
                        onChange={(e) => handleChange(e.target.value)}
                        onBlur={handleBlur}
                        disabled={disabled}
                        error={!!error}
                    >
                        <option value="" disabled>
                            Select an option
                        </option>
                        {options.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </SelectBox>
                    {isOtherSelected() && (
                        <InputBox
                            name={`${name}-other`}
                            value={otherValue}
                            onChange={(e) => {
                                setOtherValue(e.target.value);
                                onOtherValueChange?.(e.target.value);
                            }}
                            onBlur={handleBlur}
                            placeholder="Other"
                            disabled={disabled}
                            error={false}
                            autoComplete="off"
                        />
                    )}
                </div>
            )}
            {type === 'checkbox' && (
                <CheckboxGroup>
                    {options.map((opt) => (
                        <CheckboxLabel key={opt.value}>
                            <input
                                type="checkbox"
                                name={name}
                                value={opt.value}
                                checked={Array.isArray(value) ? (value as string[]).includes(opt.value) : false}
                                onChange={() => toggleCheckbox(opt.value)}
                                disabled={disabled}
                            />
                            {opt.label}
                        </CheckboxLabel>
                    ))}
                    {isOtherSelected() && (
                        <div style={{ gridColumn: '1 / -1' }}>
                            <InputBox
                                name={`${name}-other`}
                                value={otherValue}
                                onChange={(e) => {
                                    setOtherValue(e.target.value);
                                    onOtherValueChange?.(e.target.value);
                                }}
                                onBlur={handleBlur}
                                placeholder="Other"
                                disabled={disabled}
                                error={false}
                                autoComplete="off"
                            />
                        </div>
                    )}
                </CheckboxGroup>
            )}
            {type === 'segmented' && (
                <SegmentedButtonsWrapper>
                    <SegmentedButtons
                        options={options}
                        selectedOption={value as string | number}
                        updateSelectedOption={handleChange}
                        dataQ={name}
                    />
                </SegmentedButtonsWrapper>
            )}
            {error && <ErrorText isSegmented={type === 'segmented'}>{error}</ErrorText>}
        </ItemContainer>
    );
});

LeadFormItem.displayName = 'LeadFormItem';

export default LeadFormItem;
